import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Widget, WidgetInput } from "../../../widgets/types";
import { compact, minSize } from "../../dashboards/layout";
import { ActionStatus } from "../actions/ActionStatus";
import type { DeviceActions } from "../actions/useDeviceActions";
import { PageWidget } from "./PageWidget";

/** One grid unit of height, as on dashboards (the widget fixtures draw a unit as 72 px). */
const ROW_PX = 72;

/**
 * Widgets on the 12-column grid at their positions from `md` up, and one per row in reading order below that
 * (WEB-DDB-PAGE-01, WEB-DSH-VIEW-02, DSH-02): the device page and dashboards. The widgets come sorted by y, then x,
 * so the DOM order is the reading order for phones and screen readers. `inputFor` gives a widget its own input (a
 * source's members); `menu` adds actions under a widget (Add to dashboard); `actions` makes the controls work
 * (device-controls), and without it they are read-only. Gravity applies when drawing too (DDB-30), so a document
 * saved with a gap is shown without it.
 */
export function WidgetGrid({
  widgets,
  input,
  inputFor,
  menu,
  actions,
  unavailable,
  needsPlan,
  now,
  orgId,
  label,
  preview,
}: {
  widgets: Widget[];
  input: WidgetInput;
  inputFor?: (w: Widget) => WidgetInput;
  menu?: (w: Widget) => ReactNode;
  actions?: DeviceActions;
  /** A widget whose keys the device type no longer has: drawn as "no longer available" (DDB-26). */
  unavailable?: (w: Widget) => boolean;
  /** The plan a widget needs when its device's plan does not allow it: drawn as "Needs the <plan> plan" (PD-12). */
  needsPlan?: (w: Widget) => string | undefined;
  now: Date;
  orgId: string;
  label: string;
  /** Drawn for a preview: charts keep their range, nothing opens (the console's device type, WEB-DTP-02). */
  preview?: boolean;
}) {
  const withActions = (i: WidgetInput, w: Widget) => (actions ? actions.withActions(i, w) : i);
  return (
    <section
      aria-label={label}
      className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-12"
      style={{ gridAutoRows: `minmax(${ROW_PX}px, auto)` }}
    >
      {inReadingOrder(compact(widgets)).map((w) => (
        <div
          key={w.id}
          data-grid-cell={w.id}
          style={cellStyle(w)}
          className="flex min-w-0 flex-col gap-1 md:[grid-column:var(--gc)] md:[grid-row:var(--gr)]"
        >
          <div className="min-h-(--ph) flex-1 md:min-h-0">
            {unavailable?.(w) ? (
              <Unavailable widget={w} />
            ) : needsPlan?.(w) ? (
              <NeedsPlan widget={w} plan={needsPlan(w) as string} />
            ) : (
              <PageWidget
                widget={w}
                input={withActions(inputFor ? inputFor(w) : input, w)}
                now={now}
                orgId={orgId}
                onAction={actions?.onAction}
                busy={actions?.busy(w)}
                preview={preview}
              />
            )}
          </div>
          {actions && <ActionStatus widget={w} actions={actions} now={now} />}
          {menu?.(w)}
        </div>
      ))}
    </section>
  );
}

/** Gravity can change rows, so the DOM order is sorted again: by y, then x (DSH-02). */
function inReadingOrder(widgets: Widget[]): Widget[] {
  return [...widgets].sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
}

/** A saved widget the device's plan no longer allows (PD-12): kept, and back after an upgrade. */
function NeedsPlan({ widget, plan }: { widget: Widget; plan: string }) {
  const { t } = useTranslation();
  const name = t(`sharing.plan.${plan}`, { defaultValue: plan.charAt(0).toUpperCase() + plan.slice(1) });
  return (
    <div className="h-full rounded-md border border-dashed border-gray-300 bg-white p-3 text-sm">
      {widget.title && <h3 className="font-medium text-gray-900">{widget.title}</h3>}
      <p className="text-gray-700">{t("devicePage.needsPlan", { plan: name })}</p>
    </div>
  );
}

function Unavailable({ widget }: { widget: Widget }) {
  const { t } = useTranslation();
  return (
    <div className="h-full rounded-md border border-dashed border-gray-300 bg-white p-3 text-sm">
      {widget.title && <h3 className="font-medium text-gray-900">{widget.title}</h3>}
      <p className="text-gray-700">{t("devicePage.unavailable")}</p>
    </div>
  );
}

/** Grid gap between rows (gap-3). */
const GAP_PX = 12;

/**
 * The height a widget's type has on a phone, where the grid is one column (VisionPlatform web-on-phones WOP-03): at
 * least the rows of the type's smallest size (a chart three rows, a value one), whatever height it was given on a wider
 * screen, and more when its content needs it.
 */
export function phoneHeight(type: string): number {
  const rows = minSize(type).h;
  return rows * ROW_PX + (rows - 1) * GAP_PX;
}

/** The grid placement as CSS variables, applied only from `md` up; a phone uses `--ph`, its type's phone height. */
export function cellStyle(w: Widget): CSSProperties {
  const { x, y, w: width, h } = w.position;
  return {
    "--gc": `${x + 1} / span ${width}`,
    "--gr": `${y + 1} / span ${h}`,
    "--ph": `${phoneHeight(w.type)}px`,
  } as CSSProperties;
}
